import { Box, Flex, Text } from '@chakra-ui/react';
import {
  Badge,
  Button,
  ButtonIcon,
  ButtonText,
  Pagination,
  useToast
} from '@morressier/alembic';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useMemo, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faUserPlus, faXmark } from '@fortawesome/pro-regular-svg-icons';
import {
  faAngleDoubleDown,
  faAngleDoubleUp
} from '@fortawesome/pro-solid-svg-icons';
import {
  fetchReviewerSearchProperties,
  fetchSearchQueryResults,
  IDynamicFilters,
  IFilters,
  ISearchCriteria,
  ISearchQueryItems,
  ISortType
} from 'src/api/queries';
import { ISubmissionContextJournal } from '@publishing-engine/domain/views';
import { ILexiconField } from '@publishing-engine/domain/models';
import { getDateFilterCriteria } from 'src/utils/date';
import AdvancedSearch from 'src/components/Submission/ReviewTab/ReviewerSearch/AdvancedSearch';
import SortMenu, {
  SortField
} from 'src/pages/Organization/public/Journal/SubmissionUpload/components/common/Sort/SortMenu';
import FilterDrawer from 'src/pages/Organization/public/Journal/SubmissionUpload/components/common/Filters/FilterDrawer';
import Loading from 'src/pages/Status/Loading';
import NoSearchResultFound from 'src/components/Submission/ReviewTab/ReviewerSearch/NoSearchResultFound';
import ManageReviewerResult from './ManageReviewerResult';
import ReviewerSearchUserFormModal from './ReviewerSearchUserFormModal';
import {
  getFormFields,
  getPropertyStrings,
  LexiconData,
  REVIEWER_FORM_FIELD_ORDER
} from './types';
import {
  useCreateReviewerSearchUser,
  useUpdateReviewerSearchUser
} from 'src/api/mutations';

const DEFAULT_SORT = 'ReviewerFirstName_ASC';

// Lexicon is eventually consistent: give the backend time to persist before
// refetching the search results after a save/edit.
const SEARCH_REFETCH_DELAY_MS = 1000;

const SORT_FIELDS: SortField[] = [
  { value: 'ReviewerFirstName', label: 'First Name (A–Z)', direction: 'asc' },
  { value: 'ReviewerFirstName', label: 'First Name (Z–A)', direction: 'desc' },
  { value: 'RELEVANCE', label: 'Relevance', direction: 'asc' },
  {
    value: 'AverageRScore',
    label: 'Avg. R Score (High to Low)',
    direction: 'desc'
  },
  {
    value: 'AverageRScore',
    label: 'Avg. R Score (Low to High)',
    direction: 'asc'
  },
  {
    value: 'ActiveReviews',
    label: 'Active Reviews (High to Low)',
    direction: 'desc'
  },
  {
    value: 'ActiveReviews',
    label: 'Active Reviews (Low to High)',
    direction: 'asc'
  }
];

const ADVANCED_SEARCH_FIELD_ORDER = [
  ...REVIEWER_FORM_FIELD_ORDER,
  'AverageRScore',
  'ActiveReviews',
  'NumberOfTimesSubmitted',
  'AverageReviewerTurnaroundTime',
  'ReviewsInvited',
  'ReviewsNoReply',
  'ReviewsDeclined',
  'TimesUninvited',
  'TimesSelected',
  'DaysSinceLastReview',
  'LastInvitedDate',
  'PercentAcceptedNoReview',
  'PercentInvitedNoReview',
  'ReviewerOnboardedDate',
  'AcceptedNoReview',
  'Agreed',
  'ReviewerPersonDesignation',
  'TotalOpenInvites',
  'LastSubmittedDate'
];

type ISelectedFacets = Record<string, string[]>;
type IActiveFilterChip = { key: string; label: string; value: string };

const sortValueToField = (sortValue: string): SortField | null => {
  const field = SORT_FIELDS.find(
    f => `${f.value}_${f.direction.toUpperCase()}` === sortValue
  );
  return field ?? null;
};

const fieldToSortValue = (field: SortField): string =>
  `${field.value}_${field.direction.toUpperCase()}`;

interface Props {
  journal: ISubmissionContextJournal;
}

const ManageReviewerSearch = ({ journal }: Props) => {
  const toast = useToast();
  const queryClient = useQueryClient();

  const lexiconProjectId = journal.config.lexiconProjectId ?? '';
  const lexiconClassId = journal.config.lexiconClassId ?? '';
  const reviewerConfig = journal.config.cms.lexicon.fields;
  const dateFilters = journal.config.cms.lexicon.dateFilters;
  const userFilters = journal.config.cms.lexicon.userFilter;

  const getAdvancedSearchFields = (fields?: ILexiconField[]) =>
    (fields?.filter(f => f.advancedSearch !== false) ?? []).sort((a, b) => {
      const aIndex = ADVANCED_SEARCH_FIELD_ORDER.indexOf(a.predicateLabel);
      const bIndex = ADVANCED_SEARCH_FIELD_ORDER.indexOf(b.predicateLabel);
      return (
        (aIndex === -1 ? Number.MAX_SAFE_INTEGER : aIndex) -
        (bIndex === -1 ? Number.MAX_SAFE_INTEGER : bIndex)
      );
    });

  const advancedSearchFields = getAdvancedSearchFields(reviewerConfig);

  const { data: reviewerPropertiesResponse } = useQuery({
    queryKey: [
      'reviewer-search-properties',
      journal.id,
      lexiconProjectId,
      lexiconClassId
    ],
    queryFn: () =>
      fetchReviewerSearchProperties(
        journal.id,
        lexiconProjectId,
        lexiconClassId
      ),
    enabled: !!lexiconProjectId && !!lexiconClassId
  });
  const reviewerProperties = reviewerPropertiesResponse?.journal;

  const formFields = useMemo(
    () => getFormFields(reviewerProperties?.[0]?.properties),
    [reviewerProperties]
  );

  const [page, setPage] = useState<number>(1);
  const size = 10;
  const start = (page - 1) * size;

  const methods = useForm<{ dateValue: string }>({
    defaultValues: { dateValue: 'All' }
  });
  const { watch } = methods;

  const [searchCriteria, setSearchCriteria] = useState<ISearchCriteria[]>([]);
  const baseCriteriaRef = useRef<ISearchCriteria[]>([]);
  const [selectedFacets, setSelectedFacets] = useState<ISelectedFacets>({});
  const [selectedFilters, setSelectedFilters] = useState<
    Record<string, string[]>
  >({});
  const [sortValue, setSortValue] = useState<string>(DEFAULT_SORT);

  const mapSortValueToSortType = (value: string): ISortType => {
    const [keyRaw, orderRaw] = value.split('_');
    const isRelevance = keyRaw === 'RELEVANCE';

    const key: ISortType['key'] = isRelevance ? 'RELEVANCE' : 'SCHEMA';
    const order: ISortType['order'] =
      orderRaw === 'ASC' || orderRaw === 'DESC' ? orderRaw : 'DESC';

    const id = isRelevance
      ? lexiconClassId
      : reviewerProperties?.[0]?.properties?.find(
          ({ predicateLabel }) => predicateLabel === keyRaw
        )?.id;

    const dataType = isRelevance
      ? 'xsd:string'
      : reviewerProperties?.[0]?.properties?.find(
          ({ predicateLabel }) => predicateLabel === keyRaw
        )?.datatype ?? 'xsd:string';

    return { id, key, order, datatype: dataType };
  };
  const sortType = mapSortValueToSortType(sortValue);

  const handleSortChange = (newSortValue: string) => {
    setPage(1);
    setSortValue(newSortValue);
  };

  const filters: IFilters = { classFilter: [lexiconClassId] };

  const userFilterSchemaIds = new Set(
    (userFilters ?? [])
      .map(
        f =>
          reviewerProperties?.[0]?.properties.find(
            p => p.predicateLabel === f.key
          )?.id
      )
      .filter((id): id is string => !!id)
  );

  const userFilterKeys = new Set((userFilters ?? []).map(f => f.key));
  const facetFieldSchemaIds: string[] = (reviewerConfig ?? [])
    .filter(f => f.facet === true && !userFilterKeys.has(f.predicateLabel))
    .map(
      f =>
        reviewerProperties?.[0]?.properties.find(
          p => p.predicateLabel === f.predicateLabel
        )?.id
    )
    .filter((id): id is string => !!id);

  const baseSchemas: string[] = [
    ...facetFieldSchemaIds,
    ...[...userFilterSchemaIds]
  ];

  const dynamicFilters: IDynamicFilters[] = baseSchemas
    .filter(schemaId => schemaId !== '')
    .map(schemaId => {
      const isBoolean = userFilterSchemaIds.has(schemaId);
      return {
        schemaId,
        datatype: isBoolean ? 'xsd:boolean' : 'xsd:string',
        values: selectedFacets[schemaId] ?? []
      };
    });

  const selectedDateValue = watch('dateValue');

  const activeFilterChips = useMemo<IActiveFilterChip[]>(() => {
    const schemaIdToPredicateLabel = new Map(
      (reviewerProperties?.[0]?.properties ?? []).map(property => [
        property.id,
        property.predicateLabel
      ])
    );

    const facetFieldLabelByPredicate = new Map(
      (reviewerConfig ?? [])
        .filter(field => field.facet === true)
        .map(field => [field.predicateLabel, field.label])
    );

    const userFilterByPredicate = new Map(
      (userFilters ?? []).map(f => [f.key, f.label])
    );

    const facetChips = Object.entries(selectedFacets)
      .flatMap(([schemaId, values]) => {
        const predicateLabel = schemaIdToPredicateLabel.get(schemaId);
        const fieldLabel = predicateLabel
          ? facetFieldLabelByPredicate.get(predicateLabel)
          : undefined;
        const userFilterLabel = predicateLabel
          ? userFilterByPredicate.get(predicateLabel)
          : undefined;

        const formattedValues = values
          .filter(value => value !== '')
          .map(value =>
            userFilterLabel && value === 'true' ? userFilterLabel : value
          );

        if (formattedValues.length === 0) return [];

        return [
          {
            key: schemaId,
            label: userFilterLabel
              ? 'Users'
              : fieldLabel ?? predicateLabel ?? 'Filter',
            value: formattedValues.join(', ')
          }
        ];
      })
      .reduce<IActiveFilterChip[]>((acc, chip) => {
        const existing = acc.find(c => c.label === chip.label);
        if (existing) {
          existing.value = `${existing.value}, ${chip.value}`;
        } else {
          acc.push({ ...chip });
        }
        return acc;
      }, []);

    if (!selectedDateValue || selectedDateValue === 'All') {
      return facetChips;
    }

    const dateLabel =
      dateFilters?.find(f => f.key === selectedDateValue)?.label ??
      selectedDateValue;

    return [
      {
        key: `date-${selectedDateValue}`,
        label: 'Date / Period',
        value: dateLabel
      },
      ...facetChips
    ];
  }, [
    dateFilters,
    userFilters,
    reviewerConfig,
    reviewerProperties,
    selectedDateValue,
    selectedFacets
  ]);

  const buildMergedCriteria = (
    baseCriteria: ISearchCriteria[],
    dateValue: string
  ): ISearchCriteria[] => {
    let criteria = [...baseCriteria];
    if (dateValue && dateValue !== 'All') {
      criteria = [
        ...criteria,
        ...getDateFilterCriteria(dateValue, reviewerProperties, dateFilters)
      ];
    }
    return criteria;
  };

  const handleSearch = (advancedCriteria: ISearchCriteria[]) => {
    baseCriteriaRef.current = advancedCriteria;
    setSortValue('RELEVANCE_ASC');
    setPage(1);

    const dateValue = methods.getValues('dateValue');
    setSearchCriteria(
      buildMergedCriteria(advancedCriteria, dateValue as string)
    );
  };

  const handleSearchReset = () => {
    baseCriteriaRef.current = [];
    setSortValue(DEFAULT_SORT);
    setPage(1);
    const dateValue = methods.getValues('dateValue');
    setSearchCriteria(buildMergedCriteria([], dateValue as string));
  };

  const handleSearchResultsReset = () => {
    methods.setValue('dateValue', 'All');
    setSortValue(DEFAULT_SORT);
    setPage(1);
    setSelectedFacets({});
    setSelectedFilters({});
    setSearchCriteria(buildMergedCriteria(baseCriteriaRef.current, 'All'));
  };

  const handleFiltersSubmit = (nextFilters: Record<string, string[]>) => {
    const { dateValue, ...facets } = nextFilters;
    setPage(1);
    setSelectedFacets(facets);
    setSelectedFilters(nextFilters);
    methods.setValue('dateValue', dateValue?.[0] ?? 'All');
    setSortValue('RELEVANCE_ASC');
    setSearchCriteria(
      buildMergedCriteria(baseCriteriaRef.current, dateValue?.[0] ?? 'All')
    );
  };

  const [totalPages, setTotalPages] = useState(0);

  const {
    data: searchResults,
    isLoading,
    isFetching: fetching
  } = useQuery({
    queryKey: [
      'manage-reviewer-search-results',
      journal.id,
      lexiconProjectId,
      start,
      size,
      { searchCriteria, dynamicFilters, sortType, filters }
    ],
    queryFn: () =>
      fetchSearchQueryResults({
        ctxId: journal.id,
        projectId: lexiconProjectId,
        start,
        size,
        sortType,
        searchCriteria,
        dynamicFilters,
        filters,
        fuzzy: false,
        submissionId: ''
      }),
    enabled: !!journal.id && !!lexiconProjectId && !!reviewerProperties,
    keepPreviousData: true,
    onSuccess: data => {
      const pages = data?.total ? Math.ceil(data.total / size) : 0;
      setTotalPages(pages);
    },
    onError: () => {
      setTotalPages(0);
    }
  });
  const isFetching = isLoading || fetching;

  const filterGroups = useMemo(() => {
    const groups: {
      key: string;
      label: string;
      items: { value: string; name: string }[];
    }[] = [];

    if (dateFilters?.length) {
      groups.push({
        key: 'dateValue',
        label: 'Date / Period',
        items: dateFilters.map(f => ({ value: f.key, name: f.label }))
      });
    }

    if (userFilters?.length) {
      for (const uf of userFilters) {
        const schemaId = reviewerProperties?.[0]?.properties.find(
          p => p.predicateLabel === uf.key
        )?.id;
        if (schemaId) {
          groups.push({
            key: schemaId,
            label: uf.label,
            items: [{ value: 'true', name: uf.label }]
          });
        }
      }
    }

    const facetFields = (reviewerConfig ?? []).filter(
      f => f.facet === true && !userFilterKeys.has(f.predicateLabel)
    );

    for (const field of facetFields) {
      const matchedFacet = (searchResults?.propertyFacets ?? [])
        .flatMap(g => g.facets)
        .find(
          f => f.propertyName === field.predicateLabel && f.values.length > 0
        );

      if (!matchedFacet) continue;

      groups.push({
        key: matchedFacet.schemaId,
        label: field.label,
        items: matchedFacet.values.map(v => ({
          value: v.key,
          name: `${v.key} (${v.count})`
        }))
      });
    }

    return groups;
  }, [
    dateFilters,
    userFilters,
    searchResults,
    reviewerProperties,
    reviewerConfig
  ]);

  const [expandAll, setExpandAll] = useState<boolean>(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingItem, setEditingItem] = useState<ISearchQueryItems | null>(
    null
  );

  const createMutation = useCreateReviewerSearchUser(journal.id);
  const updateMutation = useUpdateReviewerSearchUser(journal.id);

  const handleSave = async (data: LexiconData): Promise<boolean> => {
    const result = editingItem
      ? await updateMutation.mutateAsync({
          email:
            getPropertyStrings(
              editingItem.properties,
              'ReviewerPrimaryEmailAddress'
            )?.[0] ?? '',
          data
        })
      : await createMutation.mutateAsync(data);

    if (result.isSuccess) {
      toast({
        variant: 'success',
        title: editingItem ? 'Reviewer updated' : 'Reviewer added'
      });
      await new Promise(resolve =>
        setTimeout(resolve, SEARCH_REFETCH_DELAY_MS)
      );
      queryClient.invalidateQueries({
        queryKey: ['manage-reviewer-search-results']
      });
    } else {
      const message =
        typeof result?.message === 'string'
          ? result.message
          : (result?.message as any)?.message || 'Internal Server Error';
      toast({
        variant: 'error',
        title: 'Error',
        description: message,
        isClosable: true
      });
    }

    return result.isSuccess;
  };

  const appliedSort = useMemo(() => sortValueToField(sortValue), [sortValue]);
  const sortOptions = useMemo(() => ({ fields: SORT_FIELDS }), []);

  return (
    <Flex direction="column" gap={3}>
      <Flex justifyContent="space-between" alignItems="center">
        <Text textStyle="headingGroup">All Reviewers - Advanced Search</Text>
        <Button
          iconLeft={<FontAwesomeIcon icon={faUserPlus} />}
          onClick={() => {
            setEditingItem(null);
            setModalOpen(true);
          }}
        >
          Add User
        </Button>
      </Flex>
      <Box
        borderBottom="1px solid"
        borderColor={'neutralPrimaryBorder'}
        padding={3}
        background={'white'}
      >
        <AdvancedSearch
          advancedSearchFields={advancedSearchFields}
          reviewerProperties={reviewerProperties}
          reviewerConfig={reviewerConfig}
          submissionContextId={journal.id}
          lexiconProjectId={lexiconProjectId}
          isFetching={isFetching}
          globalAdvancedSearchFields={[]}
          globalReviewerProperties={null}
          onSearch={handleSearch}
          onReset={handleSearchReset}
          globalLexiconConfig={false}
        />
      </Box>

      <Flex
        flexDirection="row"
        justifyContent="space-between"
        alignItems="center"
      >
        <Flex alignItems="center">
          <Text textStyle="textBody">
            Showing <strong>{searchResults?.total}</strong> Reviewers
          </Text>
          <ButtonIcon
            ariaLabel={expandAll ? 'Collapse All' : 'Expand All'}
            icon={
              <FontAwesomeIcon
                icon={!expandAll ? faAngleDoubleDown : faAngleDoubleUp}
              />
            }
            onClick={() => setExpandAll(prev => !prev)}
          />
        </Flex>
        <Flex alignItems="center" gap={2}>
          {filterGroups.length > 0 && (
            <>
              <FilterDrawer
                filterGroups={filterGroups}
                watch={watch as any}
                onSubmit={handleFiltersSubmit}
                selectedFilters={selectedFilters}
                setSelectedFilters={setSelectedFilters}
              />
              <SortMenu
                appliedSort={appliedSort}
                options={sortOptions}
                onApply={selected => {
                  if (selected) handleSortChange(fieldToSortValue(selected));
                }}
                onReset={() => {
                  setPage(1);
                  setSortValue(DEFAULT_SORT);
                }}
              />
            </>
          )}
        </Flex>
      </Flex>

      {activeFilterChips.length > 0 && (
        <Flex mt={1} alignItems="center" gap={2}>
          <Flex
            gap={2}
            wrap="wrap"
            paddingBottom={2}
            backgroundColor="surfacePrimaryFill"
            flex={1}
          >
            {activeFilterChips.map(chip => (
              <Badge
                variant="interactive"
                key={chip.key}
                paddingX={3}
                paddingY={1}
                borderRadius="md"
                colorScheme="gray"
                textTransform="none"
                whiteSpace="normal"
                wordBreak="break-word"
              >
                {`${chip.label}: ${chip.value}`}
              </Badge>
            ))}
          </Flex>
          <ButtonText variant="caution" onClick={handleSearchResultsReset}>
            <FontAwesomeIcon icon={faXmark} /> Clear Filter
          </ButtonText>
        </Flex>
      )}

      <Flex direction="column">
        {isFetching ? (
          <Box>
            <Loading />
          </Box>
        ) : searchResults?.total ? (
          <Flex flexDirection="column">
            {searchResults.items.map(item => (
              <ManageReviewerResult
                key={item.conceptId}
                item={item}
                expandAll={expandAll}
                reviewerConfig={reviewerConfig}
                onEdit={item => {
                  setEditingItem(item);
                  setModalOpen(true);
                }}
              />
            ))}
          </Flex>
        ) : (
          <NoSearchResultFound />
        )}
      </Flex>

      {totalPages > 1 && (
        <Flex justifyContent="center" py={4} key={`${totalPages}-${page}`}>
          <Pagination
            ariaLabel="Reviewer search users pagination"
            currentPage={page}
            totalPages={totalPages}
            onPageChange={setPage}
          />
        </Flex>
      )}

      {modalOpen && (
        <ReviewerSearchUserFormModal
          isOpen={modalOpen}
          onClose={() => setModalOpen(false)}
          mode={editingItem ? 'edit' : 'add'}
          item={editingItem}
          formFields={formFields}
          onSave={handleSave}
        />
      )}
    </Flex>
  );
};

export default ManageReviewerSearch;
